import { V3Pill } from "@/components/OpsSectionHead";
import { PieChart as PieChartIcon } from "lucide-react";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { ChevronDown, ChevronRight, Loader2, RefreshCw, ArrowUp, ArrowDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt0 = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmt2 = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDelta = (n) => {
  if (n == null) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const BUCKET_COLORS = {
  boarding: "#1e3a5f",
  daycare: "#10b981",
  day_boarding: "#0ea5e9",
  daycare_packages: "#8b5cf6",
  additional_services: "#f59e0b",
  other: "#6b7280",
};

// ──────────────────────────────────────────────────────────────
// 2-axis selector: Range × Slice by
// ──────────────────────────────────────────────────────────────
function getRange(rangeKey) {
  const f = (d) => d.toISOString().split("T")[0];
  const now = new Date();
  // "Closed" cutoff = yesterday, so MTD/QTD/YTD tie to Gingr's revenue_by_date
  // (today is still live/moving and is shown separately on the KPI cards).
  const closed = new Date(now); closed.setDate(closed.getDate() - 1);
  const y = now.getFullYear();
  const m = now.getMonth();
  const q = Math.floor(m / 3); // 0..3

  if (rangeKey === "mtd")        return { from: f(new Date(y, m, 1)), to: f(closed) };
  if (rangeKey === "prev_month") {
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0);
    return { from: f(start), to: f(end) };
  }
  if (rangeKey === "qtd")        return { from: f(new Date(y, q * 3, 1)), to: f(closed) };
  if (rangeKey === "prev_quarter") {
    const start = new Date(y, (q - 1) * 3, 1);
    const end = new Date(y, q * 3, 0);
    return { from: f(start), to: f(end) };
  }
  if (rangeKey === "ytd")        return { from: f(new Date(y, 0, 1)), to: f(closed) };
  if (rangeKey === "last_year") {
    // Full prior calendar year (Jan 1 — Dec 31). PY column will compare vs the year before that.
    return { from: f(new Date(y - 1, 0, 1)), to: f(new Date(y - 1, 11, 31)) };
  }
  if (rangeKey === "last_year_ytd") {
    // Same Jan 1 — today's calendar day, but one year earlier (apples-to-apples to current YTD).
    const s = new Date(y - 1, 0, 1);
    const e = new Date(y - 1, m, now.getDate());
    return { from: f(s), to: f(e) };
  }
  if (rangeKey === "last_7") {
    const s = new Date(now); s.setDate(s.getDate() - 6);
    return { from: f(s), to: f(now) };
  }
  if (rangeKey === "last_30") {
    const s = new Date(now); s.setDate(s.getDate() - 29);
    return { from: f(s), to: f(now) };
  }
  return { from: f(new Date(y, m, 1)), to: f(now) };
}

const RANGE_OPTIONS = [
  { key: "mtd",            label: "MTD" },
  { key: "prev_month",     label: "Prev Month" },
  { key: "qtd",            label: "QTD" },
  { key: "prev_quarter",   label: "Prev Quarter" },
  { key: "ytd",            label: "YTD" },
  { key: "last_year_ytd",  label: "Last Year YTD" },
  { key: "last_year",      label: "Last Year (Full)" },
  { key: "last_7",         label: "Last 7 Days" },
  { key: "last_30",        label: "Last 30 Days" },
];

const SLICE_OPTIONS = [
  { key: "auto",      label: "Auto" },
  { key: "weekly",    label: "Weekly" },
  { key: "monthly",   label: "Monthly" },
  { key: "quarterly", label: "Quarterly" },
  { key: "none",      label: "None (Total only)" },
];

// ──────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  on_track:        { bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500", label: "On Track" },
  steady:          { bg: "bg-amber-100",   text: "text-amber-800",   dot: "bg-amber-500",   label: "Steady" },
  needs_attention: { bg: "bg-red-100",     text: "text-red-700",     dot: "bg-red-500",     label: "Needs Attention" },
  na:              { bg: "bg-gray-100",    text: "text-gray-500",    dot: "bg-gray-300",    label: "—" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.na;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${s.bg} ${s.text} text-[10px] font-semibold whitespace-nowrap`}
      data-testid={`rev-status-${status}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function YoYChip({ pct }) {
  if (pct == null) return <span className="text-gray-400 text-xs">—</span>;
  const Icon = pct > 0 ? ArrowUp : pct < 0 ? ArrowDown : null;
  const color = pct > 0 ? "text-emerald-600" : pct < 0 ? "text-red-500" : "text-gray-500";
  return (
    <span className={`inline-flex items-center gap-0.5 ${color} text-xs font-semibold tabular-nums`}>
      {Icon && <Icon className="h-3 w-3" />}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function DeltaCell({ value, py, isTotal = false }) {
  if (value == null || py == null) {
    return <span className="text-gray-300 text-xs">—</span>;
  }
  const delta = value - py;
  if (Math.abs(delta) < 0.5) {
    return <span className="text-gray-500 text-xs tabular-nums">$0</span>;
  }
  const positive = delta > 0;
  const color = positive ? "text-emerald-600" : "text-red-500";
  return (
    <span className={`${color} ${isTotal ? "font-extrabold text-sm" : "font-semibold text-xs"} tabular-nums`}>
      {fmtDelta(delta)}
    </span>
  );
}

function SegmentCell({ value, isTotal = false }) {
  if (value == null || value === 0) {
    return <td className="px-1.5 py-2 text-right text-gray-300 text-xs tabular-nums">—</td>;
  }
  return (
    <td className={`px-1.5 py-2 text-right tabular-nums ${isTotal ? "font-extrabold text-gray-900 text-sm" : "font-medium text-gray-800 text-xs"}`}>
      {fmt0(value)}
    </td>
  );
}

function BucketRow({ bucketKey, bucket, segments, expanded, onToggle }) {
  const color = BUCKET_COLORS[bucketKey] || "#6b7280";
  const hasSubs = (bucket.sub_buckets || []).length > 0;
  const total = bucket.total;
  const py = bucket.yoy_total ?? bucket.yoy_value;

  return (
    <>
      <tr
        className="border-t border-gray-100 hover:bg-gray-50/60 cursor-pointer transition-colors"
        onClick={hasSubs ? onToggle : undefined}
        data-testid={`rev-row-${bucketKey}`}
      >
        <td className="px-2 py-2 sticky left-0 bg-inherit z-10">
          <div className="flex items-center gap-1.5 min-w-[150px]">
            {hasSubs ? (
              expanded ? <ChevronDown className="h-3 w-3 text-gray-400" /> : <ChevronRight className="h-3 w-3 text-gray-400" />
            ) : <span className="w-3" />}
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
            <span className="font-semibold text-gray-900 text-xs">{bucket.label}</span>
          </div>
        </td>
        {segments.map((s) => (
          <SegmentCell key={s.key} value={bucket.segments?.[s.key]} />
        ))}
        <td className="px-2 py-2 text-right font-extrabold text-gray-900 tabular-nums text-xs bg-blue-50/40">
          {fmt0(total)}
        </td>
        <td className="px-2 py-2 text-right font-semibold text-gray-700 tabular-nums text-xs bg-blue-50/20">
          {py != null ? fmt0(py) : <span className="text-gray-300">—</span>}
        </td>
        <td className="px-2 py-2 text-right">
          <DeltaCell value={total} py={py} />
        </td>
        <td className="px-2 py-2 text-right">
          <YoYChip pct={bucket.yoy_change_pct} />
        </td>
        <td className="px-2 py-2">
          <StatusBadge status={bucket.status} />
        </td>
      </tr>
      {expanded && hasSubs && bucket.sub_buckets.map((sub) => {
        const subPy = sub.yoy_amount ?? sub.yoy_total ?? sub.yoy_value;
        return (
          <tr
            key={sub.name}
            className="border-t border-gray-50 bg-gray-50/30"
            data-testid={`rev-subrow-${bucketKey}-${sub.name.replace(/\s+/g, '-').toLowerCase()}`}
          >
            <td className="px-2 py-1.5 sticky left-0 bg-gray-50/30 z-10">
              <div className="flex items-center gap-1.5 pl-6 min-w-[150px]">
                <span className="text-[11px] text-gray-600 truncate">{sub.name}</span>
                <span className="text-[10px] text-gray-400 ml-auto flex-shrink-0">{sub.items}</span>
              </div>
            </td>
            {segments.map((s) => (
              <td key={s.key} className="px-1.5 py-1.5 text-right text-[11px] text-gray-600 tabular-nums">
                {sub.segments?.[s.key] ? fmt0(sub.segments[s.key]) : <span className="text-gray-300">—</span>}
              </td>
            ))}
            <td className="px-2 py-1.5 text-right font-semibold text-gray-700 tabular-nums text-[11px] bg-blue-50/20">
              {fmt0(sub.amount)}
            </td>
            <td className="px-2 py-1.5 text-right text-gray-600 tabular-nums text-[11px] bg-blue-50/10">
              {subPy != null ? fmt0(subPy) : <span className="text-gray-300">—</span>}
            </td>
            <td className="px-2 py-1.5 text-right">
              <DeltaCell value={sub.amount} py={subPy} />
            </td>
            <td className="px-2 py-1.5 text-right">
              <YoYChip pct={sub.yoy_change_pct} />
            </td>
            <td className="px-2 py-1.5">
              <StatusBadge status={sub.status} />
            </td>
          </tr>
        );
      })}
    </>
  );
}

export default function RevenueMix() {
  const [range, setRange] = useState("mtd");
  const [slice, setSlice] = useState("auto");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const staleRefreshTimer = useRef(null);

  const fetchData = useCallback(async (opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      // Comparable-period detection: QTD + Quarterly → all 4 quarters clipped to today's day-of-quarter (apples-to-apples QoQ)
      // MTD + Monthly → all 12 months clipped to today's day-of-month (MoM apples-to-apples)
      let from, to, comparableClip = null;
      const isQtdQuarterly = range === "qtd" && slice === "quarterly";
      const isMtdMonthly   = range === "mtd" && slice === "monthly";
      if (isQtdQuarterly || isMtdMonthly) {
        const now = new Date();
        const y = now.getFullYear();
        const f = (d) => d.toISOString().split("T")[0];
        from = `${y}-01-01`;
        to = f(now);
        comparableClip = isQtdQuarterly ? "qtd" : "mtd";
      } else {
        const r = getRange(range);
        from = r.from;
        to = r.to;
      }

      let url = `${API}/ops/revenue-mix?from_date=${from}&to_date=${to}&granularity=${slice}`;
      if (comparableClip) url += `&comparable_clip=${comparableClip}`;
      const res = await axios.get(url, { timeout: 240000 });
      setData(res.data);

      // If the payload was served from a stale cache, the backend has kicked off a
      // background refresh. Poll once after a delay to pick up the fresh result —
      // unless this call was itself the auto-refresh poll.
      if (res.data?._cache?.is_stale && !opts.isStaleRefresh) {
        if (staleRefreshTimer.current) clearTimeout(staleRefreshTimer.current);
        staleRefreshTimer.current = setTimeout(() => {
          fetchData({ isStaleRefresh: true });
        }, 12000);
      }
    } catch (e) {
      setError(e?.response?.status === 403 ? "Admin access required." : "Failed to load revenue mix.");
    } finally {
      setLoading(false);
    }
  }, [range, slice]);

  useEffect(() => {
    fetchData();
    return () => {
      if (staleRefreshTimer.current) clearTimeout(staleRefreshTimer.current);
    };
  }, [fetchData]);

  const { from, to } = useMemo(() => getRange(range), [range]);
  const segments = data?.segments || [];
  const buckets = data?.buckets || {};
  const grandTotal = data?.total_revenue || 0;
  const grandYoy = data?.yoy_total_revenue;
  const grandYoyPct = data?.yoy_change_pct;
  const bucketOrder = ["boarding", "daycare", "day_boarding", "daycare_packages", "additional_services", "other"];

  const isQtdQuarterly = range === "qtd" && slice === "quarterly";
  const isMtdMonthly   = range === "mtd" && slice === "monthly";
  const comparableMode = isQtdQuarterly ? "QTD apples-to-apples" : isMtdMonthly ? "MTD apples-to-apples" : null;
  const isStale = !!data?._cache?.is_stale;
  const cacheAgeSec = data?._cache?.age_seconds ?? 0;
  const cacheAgeLabel = cacheAgeSec < 60
    ? `${cacheAgeSec}s ago`
    : cacheAgeSec < 3600
      ? `${Math.round(cacheAgeSec / 60)}m ago`
      : `${Math.round(cacheAgeSec / 3600)}h ago`;

  return (
    <Card className="border-0 shadow-md overflow-hidden" data-testid="revenue-mix-section">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="m-0"><V3Pill icon={PieChartIcon}>Revenue Mix</V3Pill></h2>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Range</span>
              <Select value={range} onValueChange={setRange}>
                <SelectTrigger className="h-7 w-[130px] text-xs" data-testid="rev-range-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RANGE_OPTIONS.map((r) => (
                    <SelectItem key={r.key} value={r.key} className="text-xs">{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Slice by</span>
              <Select value={slice} onValueChange={setSlice}>
                <SelectTrigger className="h-7 w-[140px] text-xs" data-testid="rev-slice-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SLICE_OPTIONS.map((s) => (
                    <SelectItem key={s.key} value={s.key} className="text-xs">{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2"
              onClick={fetchData}
              disabled={loading}
              data-testid="rev-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        <div className="text-xs text-gray-500 mb-3 flex items-center gap-3 flex-wrap">
          <span>{from} → {to}</span>
          <span className="text-gray-300">•</span>
          <span>Matches Gingr "Charges by Account Code"</span>
          <span className="text-gray-300">•</span>
          <span>YoY vs same period 1 yr ago</span>
          {comparableMode && (
            <>
              <span className="text-gray-300">•</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold text-[10px]" data-testid="rev-comparable-badge">
                {comparableMode}
              </span>
            </>
          )}
          {isStale && !loading && (
            <>
              <span className="text-gray-300">•</span>
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold text-[10px]"
                data-testid="rev-stale-badge"
                title={`Showing data cached ${cacheAgeLabel}. Refreshing in background…`}
              >
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
                Refreshing · cached {cacheAgeLabel}
              </span>
            </>
          )}
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center py-12 gap-2 text-gray-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Building breakdown from Gingr…
          </div>
        ) : error && !data ? (
          <div className="text-center py-8 text-red-500 text-sm">{error}</div>
        ) : !data || segments.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">No data for this period.</div>
        ) : (
          <div className="relative">
            {loading && (
              <div className="absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden bg-blue-100" data-testid="rev-progress-bar">
                <div className="h-full w-1/3 bg-blue-500 animate-[progressSlide_1.2s_ease-in-out_infinite]" />
              </div>
            )}
            <div className={`overflow-x-auto -mx-4 px-4 transition-opacity duration-200 ${loading ? "opacity-60 pointer-events-none" : "opacity-100"}`}>
              <table className="w-full text-sm border-collapse" data-testid="rev-matrix">
              <thead>
                <tr className="bg-gray-50 border-b-2 border-gray-200">
                  <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                    Service
                  </th>
                  {segments.map((s) => (
                    <th key={s.key} className="px-1.5 py-2 text-right text-[10px] font-bold text-gray-600 uppercase tracking-wider min-w-[60px]">
                      {s.label}
                    </th>
                  ))}
                  <th className="px-2 py-2 text-right text-[10px] font-bold text-blue-700 uppercase tracking-wider bg-blue-50/50 min-w-[70px]">
                    Total
                  </th>
                  <th className="px-2 py-2 text-right text-[10px] font-bold text-blue-600 uppercase tracking-wider bg-blue-50/30 min-w-[70px]">
                    PY $
                  </th>
                  <th className="px-2 py-2 text-right text-[10px] font-bold text-gray-600 uppercase tracking-wider min-w-[60px]">
                    $ Δ
                  </th>
                  <th className="px-2 py-2 text-right text-[10px] font-bold text-gray-600 uppercase tracking-wider min-w-[55px]">
                    YoY %
                  </th>
                  <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wider min-w-[100px]">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {bucketOrder.filter((k) => buckets[k]).map((key) => (
                  <BucketRow
                    key={key}
                    bucketKey={key}
                    bucket={buckets[key]}
                    segments={segments}
                    expanded={!!expanded[key]}
                    onToggle={() => setExpanded((p) => ({ ...p, [key]: !p[key] }))}
                  />
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gradient-to-r from-blue-50 to-indigo-50" data-testid="rev-total-row">
                  <td className="px-2 py-2.5 sticky left-0 bg-gradient-to-r from-blue-50 to-indigo-50 z-10">
                    <div className="flex items-center gap-1.5">
                      <span className="font-extrabold text-gray-900 text-xs">Total Revenue</span>
                      <span className="text-[10px] text-gray-500">({data?.invoice_count || 0} inv)</span>
                    </div>
                  </td>
                  {segments.map((s) => (
                    <td key={s.key} className="px-1.5 py-2.5 text-right font-extrabold text-gray-900 tabular-nums text-xs">
                      {fmt0(data?.segment_totals?.[s.key])}
                    </td>
                  ))}
                  <td className="px-2 py-2.5 text-right font-extrabold text-blue-700 tabular-nums text-sm bg-blue-100/50">
                    {fmt0(grandTotal)}
                  </td>
                  <td className="px-2 py-2.5 text-right font-bold text-blue-600 tabular-nums text-xs bg-blue-100/30">
                    {grandYoy != null ? fmt0(grandYoy) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-2 py-2.5 text-right">
                    <DeltaCell value={grandTotal} py={grandYoy} isTotal />
                  </td>
                  <td className="px-2 py-2.5 text-right">
                    <YoYChip pct={grandYoyPct} />
                  </td>
                  <td className="px-2 py-2.5">
                    <StatusBadge status={data?.status} />
                  </td>
                </tr>
              </tfoot>
            </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
